import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase/client';
import { useAuthStore } from '../../stores/auth-store';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

interface InvitePreview {
    status: string;
    invitation_type: string;
    page_id: string | null;
    page_name: string | null;
    page_slug: string | null;
    sender_name: string;
    relationship: string | null;
    personal_message: string | null;
    is_admin_invite: boolean;
}

/**
 * JoinPage — preview a memorial invitation, then accept only after an explicit choice.
 * Personal accounts sign in or sign up with a phone number.
 */
export function JoinPage() {
    const { t } = useTranslation();
    const { inviteCode = '' } = useParams<{ inviteCode: string }>();
    const navigate = useNavigate();
    const { user, profile, signOut } = useAuthStore();
    const [preview, setPreview] = useState<InvitePreview | null>(null);
    const [phase, setPhase] = useState<'loading' | 'ready' | 'working' | 'done' | 'error'>('loading');
    const [error, setError] = useState('');
    const [resultText, setResultText] = useState('');

    useEffect(() => {
        if (!inviteCode) {
            setPhase('error');
            setError(t('contacts.join.invalidLink', 'Invalid invitation link'));
            return;
        }
        let cancelled = false;
        (async () => {
            const { data, error: invokeError } = await supabase.functions.invoke('accept-invitation', {
                body: { action: 'preview', invite_code: inviteCode },
            });
            if (cancelled) return;
            if (invokeError || data?.error) {
                setPhase('error');
                setError(data?.error || t('contacts.join.invalidLink', 'Invalid invitation link'));
                return;
            }
            setPreview(data as InvitePreview);
            setPhase('ready');
        })();
        return () => {
            cancelled = true;
        };
    }, [inviteCode, t]);

    const rememberReturn = () => {
        sessionStorage.setItem('pending_invite_code', inviteCode);
        sessionStorage.setItem('candle_redirect_to', `/join/${inviteCode}`);
    };

    const accept = async () => {
        setPhase('working');
        const { data, error: invokeError } = await supabase.functions.invoke('accept-invitation', {
            body: { action: 'accept', invite_code: inviteCode },
        });
        if (invokeError || data?.error) {
            setPhase('error');
            setError(data?.error || t('contacts.join.failed', 'Failed to accept invitation'));
            return;
        }
        setResultText(
            data?.action_result === 'added_as_page_admin'
                ? t('contacts.join.addedAsAdmin', 'You have been added as a page admin.')
                : t('contacts.join.addedAsMember', 'You have been added as a page member.'),
        );
        setPhase('done');
        const target = data?.page_id ? `/memorial/${preview?.page_slug || data.page_id}` : '/home';
        setTimeout(() => navigate(target), 1200);
    };

    const decline = async () => {
        setPhase('working');
        const { data, error: invokeError } = await supabase.functions.invoke('accept-invitation', {
            body: { action: 'decline', invite_code: inviteCode },
        });
        if (invokeError || data?.error) {
            setPhase('error');
            setError(data?.error || t('contacts.join.failed', 'Failed to decline invitation'));
            return;
        }
        setResultText(t('contacts.join.declined', 'You declined this invitation.'));
        setPhase('done');
    };

    const accountName = profile?.fullname || user?.phone || t('contacts.join.thisAccount', 'this account');
    const pending = preview?.status === 'pending';

    return (
        <div className="flex min-h-screen items-center justify-center bg-background px-4">
            <Card className="w-full max-w-md">
                <CardContent className="flex flex-col items-center py-12 text-center">
                    {phase === 'loading' || phase === 'working' ? (
                        <>
                            <Loader2 className="mb-4 h-12 w-12 animate-spin text-primary" />
                            <h2 className="text-xl font-semibold">{t('contacts.join.pleaseWait', 'Please wait...')}</h2>
                        </>
                    ) : null}

                    {phase === 'ready' && preview && (
                        <>
                            <h2 className="text-xl font-semibold">{preview.page_name || t('contacts.join.memorial', 'A memorial page')}</h2>
                            <p className="mt-2 text-sm text-muted-foreground">
                                {t('contacts.join.invitedBy', '{{name}} invited you.', { name: preview.sender_name })}
                            </p>
                            {preview.relationship && (
                                <p className="mt-2 text-sm">{t('contacts.join.relationship', 'Relationship: {{relationship}}', { relationship: preview.relationship })}</p>
                            )}
                            {preview.personal_message && (
                                <p className="mt-3 text-sm italic text-muted-foreground">“{preview.personal_message}”</p>
                            )}
                            {preview.is_admin_invite && (
                                <p className="mt-3 text-sm">{t('contacts.join.adminNote', 'Accepting includes help managing this page.')}</p>
                            )}
                            {!pending && (
                                <p className="mt-4 text-sm text-muted-foreground">{t('contacts.join.unavailable', 'This invitation is {{status}}.', { status: preview.status })}</p>
                            )}
                            {pending && user && (
                                <>
                                    <p className="mt-4 text-sm">{t('contacts.join.acceptingAs', 'Accepting as {{name}}.', { name: accountName })}</p>
                                    <div className="mt-6 flex gap-3">
                                        <Button variant="outline" onClick={() => void decline()}>{t('contacts.join.decline', 'Decline')}</Button>
                                        <Button onClick={() => void accept()}>{t('contacts.join.accept', 'Accept')}</Button>
                                    </div>
                                    <Button
                                        variant="ghost"
                                        className="mt-3"
                                        onClick={() => {
                                            rememberReturn();
                                            void signOut();
                                            navigate(`/sign-in?redirectTo=${encodeURIComponent(`/join/${inviteCode}`)}`);
                                        }}
                                    >
                                        {t('contacts.join.differentAccount', 'Use a different account')}
                                    </Button>
                                </>
                            )}
                            {pending && !user && (
                                <div className="mt-6 flex gap-3">
                                    <Button
                                        variant="outline"
                                        onClick={() => {
                                            rememberReturn();
                                            navigate(`/sign-in?redirectTo=${encodeURIComponent(`/join/${inviteCode}`)}`);
                                        }}
                                    >
                                        {t('common.signIn', 'Sign In')}
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            rememberReturn();
                                            navigate(`/sign-up?from=invite&redirectTo=${encodeURIComponent(`/join/${inviteCode}`)}`);
                                        }}
                                    >
                                        {t('contacts.join.createAccount', 'Create Account')}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}

                    {phase === 'done' && (
                        <>
                            <CheckCircle className="mb-4 h-12 w-12 text-green-500" />
                            <p className="text-sm text-muted-foreground">{resultText}</p>
                        </>
                    )}

                    {phase === 'error' && (
                        <>
                            <XCircle className="mb-4 h-12 w-12 text-destructive" />
                            <h2 className="text-xl font-semibold">{t('contacts.join.errorTitle', 'Invitation Error')}</h2>
                            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
                            <Button className="mt-4" onClick={() => navigate('/home')}>{t('contacts.join.goHome', 'Go Home')}</Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

/**
 * Hook to process pending invite codes after authentication.
 * Call this in the app shell or auth callback.
 */
export function usePendingInvite() {
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!user) return;
        const pendingCode = sessionStorage.getItem('pending_invite_code');
        if (!pendingCode) return;
        if (location.pathname === `/join/${pendingCode}`) {
            sessionStorage.removeItem('pending_invite_code');
            return;
        }
        if (location.pathname.startsWith('/sign-in') || location.pathname.startsWith('/sign-up') || location.pathname.startsWith('/verify-otp')) {
            return;
        }
        sessionStorage.removeItem('pending_invite_code');
        navigate(`/join/${pendingCode}`);
    }, [user, navigate, location.pathname]);
}
